import React from 'react'
import { ChevronDown } from 'lucide-react'
import { TaskDropdown } from '../../TaskDropdown'
import type { DetailsChipId } from '../useDetailsSections'

export interface DetailsChipProps {
  id: DetailsChipId
  /** Das Zeichen links im Chip — dasselbe wie an der **Marke** der Zeile. */
  icon: React.ReactNode
  /**
   * Was auf dem Chip steht. **Der Wert, sobald einer da ist** — sonst die
   * Beschriftung (entschieden 2026-09-19). Ein Knoten und keine Zeichenkette,
   * weil Tags als Pillen und die **Belohnung** mit ihrem Katalogbild kommen.
   */
  children: React.ReactNode
  /** Ob ein Wert dasteht — färbt den Chip und schaltet die Beschriftung ab. */
  filled: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Der Inhalt des Popovers. */
  content: React.ReactNode
  /** Breite des Kastens, wie an der Marke der Zeile. */
  width?: number
}

/**
 * **Ein Chip im Kopf der Aufgaben-Details** — Zeichen, Wert (oder Beschriftung),
 * Chevron; ein Klick öffnet den Inhalt als Popover.
 *
 * Die Hülle ist `TaskDropdown`, dieselbe wie an der **Marke** der Zeile, und der
 * Inhalt trägt deshalb `placement="badge"`: Der Chip IST ein Popover-Griff, kein
 * offener Bereich. Damit gilt dort auch weiterhin der Fuß mit „Bestätigen" und
 * Papierkorb — die neue Fuß-Regel der Aufgaben-Details (ADR 0026) gilt nur den
 * vier Kästen im Rumpf, die offen stehen.
 *
 * @example
 * <DetailsChip id="tags" icon={<TagIcon size={16} />} filled={ids.length > 0}
 *   open={open === 'tags'} onOpenChange={…} content={<TagDropdownContent … />}>
 *   {ids.length ? pillen : t('taskDetails.section.tags')}
 * </DetailsChip>
 */
export const DetailsChip: React.FC<DetailsChipProps> = ({
  id, icon, children, filled, open, onOpenChange, content, width,
}) => (
  <div
    className={`details-chip${filled ? ' details-chip--filled' : ''}${open ? ' details-chip--open' : ''}`}
    data-chip={id}
  >
    <TaskDropdown
      open={open}
      onOpenChange={onOpenChange}
      side="bottom"
      align="start"
      contentStyle={width ? { width } : undefined}
      trigger={
        <button type="button" className="details-chip__trigger">
          <span className="details-chip__icon">{icon}</span>
          <span className="details-chip__label">{children}</span>
          <ChevronDown size={16} className="details-chip__chevron" aria-hidden="true" />
        </button>
      }
    >
      {content}
    </TaskDropdown>
  </div>
)
